import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, X } from "lucide-react";
import { registerAttachmentViewer } from "@/utils/attachmentViewer";
import { isNativeApp } from "@/utils/mobileShell";

const fetchFile = async (url, signal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
};

const nameFromUrl = (url = "") => {
  try {
    return decodeURIComponent(new URL(url, window.location.href).pathname.split("/").pop());
  } catch (_) {
    return "";
  }
};

// Near full-screen dialog that previews images, PDFs and text files on the same page.
// Opened through openAttachment() in utils/attachmentViewer; mounted once in App.
export default function AttachmentViewer() {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const [source, setSource] = useState(null);
  const [file, setFile] = useState(null);
  const [failed, setFailed] = useState(false);

  // Native app WebViews can't show embedded PDFs, so there files keep opening the native way.
  useEffect(() => (isNativeApp() ? undefined : registerAttachmentViewer(setSource)), []);

  // The file is fetched once and shown from memory; closing aborts the request and frees it.
  useEffect(() => {
    if (!source) return undefined;
    const controller = new AbortController();
    let objectUrl = null;
    setFile(null);
    setFailed(false);
    if (!dialogRef.current.open) dialogRef.current.showModal();

    (source.load ? source.load() : fetchFile(source.url, controller.signal))
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setFile({ url: objectUrl, type: (blob.type || "").split(";")[0].toLowerCase() });
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source]);

  const close = () => dialogRef.current?.close();
  const name = source?.name || nameFromUrl(source?.url) || t("attachment");
  const message = "max-w-sm text-center text-sm font-medium text-slate-600 dark:text-slate-300";

  let body;
  if (failed) {
    body = (
      <div className={message}>
        <p>{t("attachment_viewer_load_failed")}</p>
        {source?.url && (
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block font-semibold text-emerald-700 underline dark:text-emerald-400"
          >
            {t("open_in_new_tab")}
          </a>
        )}
      </div>
    );
  } else if (!file) {
    body = <Loader2 aria-label={t("loading")} className="h-7 w-7 animate-spin text-emerald-700" />;
  } else if (file.type.startsWith("image/")) {
    body = <img src={file.url} alt={name} className="max-h-full max-w-full object-contain" />;
  } else if (file.type === "application/pdf" || file.type.startsWith("text/")) {
    body = <iframe src={file.url} title={name} className="h-full w-full bg-white" />;
  } else {
    body = <p className={message}>{t("attachment_viewer_no_preview")}</p>;
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="attachment-viewer-title"
      onClose={() => setSource(null)}
      // Backdrop clicks land on the dialog itself; clicks on its content don't.
      onClick={(event) => event.target === dialogRef.current && close()}
      className="m-auto h-[90vh] max-h-none w-[min(96vw,72rem)] max-w-none overflow-hidden rounded-2xl bg-white p-0 shadow-2xl backdrop:bg-slate-900/60 open:flex open:flex-col open:animate-dialog-in motion-reduce:animate-none dark:bg-slate-800"
    >
      <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <h2
          id="attachment-viewer-title"
          className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800 dark:text-white"
          title={name}
        >
          {name}
        </h2>
        {file && (
          <a
            href={file.url}
            download={name}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors duration-200 hover:border-emerald-500 hover:text-emerald-700 dark:border-slate-600 dark:text-slate-200"
          >
            <Download size={14} />
            {t("download")}
          </a>
        )}
        <button
          type="button"
          onClick={close}
          aria-label={t("close")}
          title={t("close")}
          className="rounded-lg p-1.5 text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          <X size={18} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50 p-4 dark:bg-slate-900">
        {body}
      </div>
    </dialog>
  );
}
